/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import mapValues from "lodash/mapValues";
import sortBy from "lodash/sortBy";

/** What the map's own label and road layers are drawn for. */
export interface LayerSetVisibility {
  mapLabels?: boolean;
  mapRoads?: boolean;
}

interface LayerSet {
  name: string;
  groupNames?: string[];
  typeNames?: string[];
  /** Draw order within the sets, lowest first. */
  z: number;
  isVisible: (visibility: LayerSetVisibility) => boolean;
}

/**
 * Criteria for identifying which set a basemap layer belongs to, in priority
 * order, i.e. the first matching entry is the one applied. Here a 'set' is not
 * a mapbox concept and means nothing outside this module.
 */
const LAYER_SETS: LayerSet[] = [
  {
    name: "roadLabels",
    groupNames: ["road-labels"],
    z: 3,
    isVisible: ({ mapLabels, mapRoads }) => mapRoads && mapLabels,
  },
  {
    name: "roads",
    groupNames: [
      "road",
      "roads",
      "bridges",
      "tunnels",
      "surface",
      "surface-icons",
    ],
    z: 2,
    isVisible: ({ mapRoads }) => mapRoads,
  },
  {
    name: "labelSymbols",
    typeNames: ["symbol"],
    z: 4,
    isVisible: ({ mapLabels }) => mapLabels,
  },
  {
    name: "labels",
    groupNames: ["label", "labels"],
    z: 5,
    isVisible: ({ mapLabels }) => mapLabels,
  },
];

const emptyLayersBySet = (): Record<string, any[]> => ({
  other: [],
  roads: [],
  roadLabels: [],
  labels: [],
  labelSymbols: [],
});

/** Groups a style's layers into the sets above, leaving the rest in `other`. */
export const getLayersBySet = (style: any = {}): Record<string, any[]> => {
  const getLayerSet = (layerGroupName = "", layerTypeName = "") => {
    const normalisedLayerGroupName = layerGroupName.toLowerCase();
    const normalisedLayerTypeName = layerTypeName.toLowerCase();

    return LAYER_SETS.find(
      (layerSet) =>
        (layerSet.groupNames?.some((gn) =>
          normalisedLayerGroupName.includes(gn)
        ) ??
          true) &&
        (layerSet.typeNames?.some((tn) =>
          normalisedLayerTypeName.includes(tn)
        ) ??
          true)
    );
  };

  const { layers = [], metadata } = style ?? {};

  // Styles without mapbox:groups (Mapbox's own styles among them) are grouped
  // by source-layer and type alone.
  const setByGroupKey = mapValues(
    metadata?.["mapbox:groups"] ?? {},
    (layerGroup: any) => getLayerSet(layerGroup.name)
  );

  return layers.reduce((acc, layer) => {
    const {
      "source-layer": sourceLayer,
      metadata: layerMetadata,
      type: layerType,
    } = layer;

    const normalisedSourceLayer = sourceLayer?.toLowerCase() || "";
    const normalisedGroupName =
      layerMetadata?.["mapbox:group"]?.toLowerCase() || "";
    const setName =
      (
        setByGroupKey[normalisedGroupName] ??
        getLayerSet(normalisedSourceLayer, layerType)
      )?.name ?? "other";

    acc[setName].push(layer);

    return acc;
  }, emptyLayersBySet());
};

// Layer-manager inserts data layers below the first style layer whose id
// matches /custom-layers|label|place|poi/, and moves them back there on every
// update. Once the sets are on top that is a road label inside the roads set,
// so a sourceless layer by this name is kept just under the sets instead.
const LAYER_MANAGER_ANCHOR_ID = "custom-layers";

/**
 * Shows or hides a loaded map's label and road layers. With `reorder` (the
 * default) each set is also moved to the top in `z` order, above the anchor
 * that keeps layer-manager's data layers below them; pass false to leave the
 * layer order alone.
 */
export const applyLayerSetVisibility = (
  map: any,
  visibility: LayerSetVisibility,
  { reorder = true }: { reorder?: boolean } = {}
) => {
  // Not isStyleLoaded(): that is false while any tile is in flight, which on
  // a map with data layers is most of the time, so a toggle applied then was
  // dropped. getStyle() throws only until the style JSON has loaded, which is
  // all setLayoutProperty and moveLayer need.
  let style;
  try {
    style = map?.getStyle?.();
  } catch {
    return;
  }
  if (!style) {
    return;
  }

  // Added before the sets are raised so it ends up directly below them. Style
  // swaps drop it, hence the check on every call.
  if (reorder && !map.getLayer(LAYER_MANAGER_ANCHOR_ID)) {
    map.addLayer({
      id: LAYER_MANAGER_ANCHOR_ID,
      type: "background",
      layout: { visibility: "none" },
    });
  }

  const layersBySet = getLayersBySet(style);

  sortBy(LAYER_SETS, "z").forEach((layerSet) =>
    layersBySet[layerSet.name].forEach((layer) => {
      map.setLayoutProperty(
        layer.id,
        "visibility",
        layerSet.isVisible(visibility) ? "visible" : "none"
      );

      if (reorder) {
        map.moveLayer(layer.id);
      }
    })
  );
};

/**
 * The same visibility applied to a style document, so hidden layers never
 * flash up while the style loads.
 */
export const withLayerSetVisibility = (
  style: any,
  visibility: LayerSetVisibility
) => {
  const layersBySet = getLayersBySet(style);
  const layersById = {};

  LAYER_SETS.forEach((layerSet) => {
    layersBySet[layerSet.name].forEach((layer) => {
      layersById[layer.id] = {
        ...layer,
        layout: {
          ...(layer.layout ?? {}),
          visibility: layerSet.isVisible(visibility) ? "visible" : "none",
        },
      };
    });
  });

  return {
    ...style,
    layers: (style.layers ?? []).map((layer) => layersById[layer.id] ?? layer),
  };
};
